/**
 * GM-key one-time reveal — the screen shown exactly once, right after room
 * creation (M3.5 brief: "the single worst place in the product to have a
 * mediocre UX"). The key is never shown again (POST /api/rooms returns it
 * once); losing it strands the GM behind the re-key flow with nothing to
 * re-enter. So this panel is deliberately hard to dismiss carelessly:
 *
 *  - the key and both links are selectable text with copy buttons;
 *  - continuing is gated behind an explicit "I've saved my GM key" button —
 *    there is no other way forward, no auto-advance, no timeout;
 *  - the GM link (with #gm=) is presented as the thing to bookmark, because
 *    a bookmark is how a real table's GM will actually come back.
 *
 * The clipboard is injectable because jsdom has none; the fallback for a
 * clipboard-less browser is the selectable text itself.
 */

export interface GmKeyRevealInfo {
  code: string;
  gmKey: string;
  /** Player-facing join link (no key). */
  joinUrl: string;
  /** GM link carrying #gm= — the bookmarkable way back in. */
  gmUrl: string;
}

export interface GmKeyRevealCallbacks {
  /** GM confirmed they saved the key; proceed into the table. */
  onContinue(): void;
  /** Injectable for tests; defaults to navigator.clipboard. */
  copyText?: (text: string) => Promise<void>;
}

function copyRow(
  labelText: string,
  value: string,
  valueTestid: string,
  copyTestid: string,
  copyText: (text: string) => Promise<void>,
): HTMLElement {
  const row = document.createElement("p");
  row.className = "reveal-row";

  const label = document.createElement("strong");
  label.textContent = `${labelText}: `;
  const code = document.createElement("code");
  code.dataset["testid"] = valueTestid;
  code.textContent = value;

  const copy = document.createElement("button");
  copy.type = "button";
  copy.dataset["testid"] = copyTestid;
  copy.textContent = "Copy";
  copy.setAttribute("aria-label", `Copy ${labelText}`);
  copy.addEventListener("click", () => {
    void copyText(value).then(
      () => {
        copy.textContent = "Copied!";
        copy.dataset["copied"] = "true";
      },
      () => {
        // Clipboard refused (permissions, http): the selectable text is the
        // fallback; say so instead of pretending.
        copy.textContent = "Select & copy manually";
      },
    );
  });

  row.append(label, code, copy);
  return row;
}

/**
 * Render the reveal into `root` (replaces children — this IS the panel
 * until the GM confirms). `onContinue` fires only from the explicit
 * confirmation button.
 */
export function renderGmKeyReveal(
  root: HTMLElement,
  info: GmKeyRevealInfo,
  cb: GmKeyRevealCallbacks,
): void {
  root.replaceChildren();
  const copyText =
    cb.copyText ?? ((text: string) => navigator.clipboard.writeText(text));

  const panel = document.createElement("section");
  panel.dataset["testid"] = "gm-key-reveal";
  panel.setAttribute("aria-labelledby", "gm-key-reveal-title");

  const title = document.createElement("h2");
  title.id = "gm-key-reveal-title";
  title.textContent = `Table created — room code ${info.code}`;
  panel.appendChild(title);

  const warning = document.createElement("p");
  warning.setAttribute("role", "alert");
  warning.textContent =
    "This is the only time your GM key will be shown. Save it now — " +
    "without it, a reload without your GM link demotes you to a player.";
  panel.appendChild(warning);

  panel.appendChild(
    copyRow("GM key", info.gmKey, "gm-key", "copy-gm-key", copyText),
  );
  panel.appendChild(
    copyRow("Your GM link (bookmark this)", info.gmUrl, "gm-url", "copy-gm-url", copyText),
  );
  panel.appendChild(
    copyRow("Player join link", info.joinUrl, "join-url", "copy-join-url", copyText),
  );

  const proceed = document.createElement("button");
  proceed.type = "button";
  proceed.dataset["testid"] = "gm-key-saved";
  proceed.textContent = "I’ve saved my GM key — enter the table";
  proceed.addEventListener("click", () => cb.onContinue());
  panel.appendChild(proceed);

  root.appendChild(panel);
}
